// @ts-check

/**
 * @file Provides the shared invoice card used by invoice lists.
 */
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams} from "expo-router"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"

import Button from "../button"
import Card from "../card"
import Link from "../link"
import buildRoutePath from "../../shared/build-route-path"
import formatDate from "../../shared/format-date"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"

/** @import {BoardThemeColors} from "../../shared/theme" */
/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {import("../../frontend-models/invoice").default} Invoice */

/** @type {Record<string, object>} */
const styles = {}
/** @type {Record<string, object>} */
const badgeStyles = {}

/**
 * Resolves background + text tokens for an invoice state badge from the
 * active palette.
 * @param {string} state Raw invoice state value.
 * @param {BoardThemeColors} colors Palette returned from `useThemeColors`.
 * @returns {{background: string, text: string}} Badge background fill and text color.
 */
function invoiceStateBadgeColors(state, colors) {
  if (state === "paid" || state === "registered_as_paid") {
    return {background: colors.successBackground, text: colors.successText}
  }
  if (state === "finished") {
    return {background: colors.infoBackground, text: colors.infoText}
  }
  if (state === "sent") {
    return {background: colors.warningBackground, text: colors.warningText}
  }
  return {background: colors.surfaceMuted, text: colors.textMuted}
}

/**
 * Renders the colored state badge shown next to the invoice number.
 * @param {string} state Raw invoice state.
 * @param {string} label Translated state label.
 * @param {string} testID Parent card test id.
 * @param {BoardThemeColors} colors Palette returned from `useThemeColors`.
 * @param {string} themeName Theme identifier used to key cached styles per theme.
 * @returns {ReactNode} Pill containing the translated state label.
 */
function renderInvoiceStateBadge(state, label, testID, colors, themeName) {
  const badgeColors = invoiceStateBadgeColors(state, colors)
  const styleKey = `invoiceStateBadge-${themeName}-${state}`
  return (
    <View
      style={badgeStyles[styleKey] ||= {
        backgroundColor: badgeColors.background,
        borderRadius: 999,
        paddingHorizontal: 10,
        paddingVertical: 3
      }}
      testID={`${testID}/state`}
    >
      <Text
        style={badgeStyles[`${styleKey}-text`] ||= {
          color: badgeColors.text,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.4,
          textTransform: "uppercase"
        }}
      >
        {label}
      </Text>
    </View>
  )
}

/**
 * Formats cents as a localized amount or `-` when missing.
 * @param {number | null | undefined} value Amount in cents.
 * @returns {string} Localized amount label.
 */
function formatMoneyFromCents(value) {
  if (value === null || value === undefined) return "-"
  return Number(value / 100).toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  })
}

/**
 * @callback InvoiceCardDeleteHandler
 * @param {Invoice} invoice Invoice selected for deletion.
 * @returns {unknown}
 */

/**
 * @typedef {object} InvoiceCardProps
 * @property {Invoice} invoice Invoice to render.
 * @property {InvoiceCardDeleteHandler | undefined} [onDeletePress] Delete callback.
 * @property {string} testID Stable test id for the card root.
 */

/** @typedef {Record<string, never>} InvoiceCardState */

/**
 * Shared invoice card.
 * @augments {ShapeComponent<InvoiceCardProps, InvoiceCardState>}
 */
class InvoiceCard extends ShapeComponent {
  static propTypes = propTypesExact({
    invoice: PropTypes.object.isRequired,
    onDeletePress: PropTypes.func,
    testID: PropTypes.string.isRequired
  })

  static defaultProps = {
    onDeletePress: undefined
  }

  /** @type {Record<string, string | string[]>} */
  params = this.hookField()
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires translations and theme colors into the card. */
  setup() {
    useLocale()
    this.params =
      /** @type {Record<string, string | string[]>} */ useLocalSearchParams()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the invoice card.
   * @returns {ReactNode} Invoice card content.
   */
  render() {
    const {invoice, onDeletePress, testID} = this.p
    const invoiceId = invoice.id()
    const invoicePath = buildRoutePath(this.tt.params, `/invoices/${invoiceId}`)
    const invoiceEditPath = buildRoutePath(this.tt.params, `/invoices/${invoiceId}/edit`)
    const creditor = invoice.creditor()
    const organization = invoice.organization()
    const state = invoice.state() || ""
    const stateLabel = invoice.translatedState() || state
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <Card
        style={styles.cardSpacing ||= {
          marginBottom: 12
        }}
        testID={testID}
      >
        <View
          style={styles.header ||= {
            alignItems: "flex-start",
            flexDirection: "row",
            gap: 8
          }}
          testID={`${testID}/header`}
        >
          <View
            style={styles.headerTitle ||= {
              alignItems: "center",
              flex: 1,
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 8
            }}
          >
            <Link
              href={invoicePath}
              testID={`${testID}/invoiceNoLink`}
            >
              <Text
                style={styles.invoiceNo ||= {
                  fontSize: 16,
                  fontWeight: 700
                }}
                testID={`${testID}/invoiceNo`}
              >
                {invoice.invoiceNo() || `#${invoiceId}`}
              </Text>
            </Link>
            {stateLabel ? renderInvoiceStateBadge(state, stateLabel, testID, colors, themeName) : null}
            {(invoice.translatedInvoiceType() || invoice.invoiceType()) &&
              <Text
                style={styles[`invoiceType-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 12,
                  fontWeight: 600
                }}
                testID={`${testID}/invoiceType`}
              >
                {invoice.translatedInvoiceType() || invoice.invoiceType()}
              </Text>
            }
          </View>
          {onDeletePress &&
            <Button
              href={invoiceEditPath}
              iconName="pen-to-square"
              label={_("Edit")}
              testID={`${testID}/editButton`}
              variant="outline"
            />
          }
          {onDeletePress &&
            <Button
              iconName="trash"
              label={_("Delete")}
              onPress={this.tt.onDeleteButtonPress}
              testID={`${testID}/deleteButton`}
              variant="destructive-outline"
            />
          }
        </View>
        {invoice.title() &&
          <Text
            style={styles[`title-${themeName}`] ||= {
              color: colors.textMuted,
              marginTop: 6
            }}
            testID={`${testID}/title`}
          >
            {invoice.title()}
          </Text>
        }
        <View
          style={styles.parties ||= {
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 16,
            marginTop: 10
          }}
          testID={`${testID}/parties`}
        >
          <View testID={`${testID}/creditor`}>
            <Text
              style={styles[`creditorLabel-${themeName}`] ||= {
                color: colors.placeholder,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: "uppercase"
              }}
              testID={`${testID}/creditor/label`}
            >
              {_("Creditor")}
            </Text>
            <Text
              style={styles.creditorValue ||= {
                fontSize: 13,
                fontWeight: 600
              }}
              testID={`${testID}/creditor/name`}
            >
              {creditor ? creditor.name() : "-"}
            </Text>
          </View>
          <View testID={`${testID}/debitor`}>
            <Text
              style={styles[`debitorLabel-${themeName}`] ||= {
                color: colors.placeholder,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: "uppercase"
              }}
              testID={`${testID}/debitor/label`}
            >
              {_("Debitor")}
            </Text>
            <Text
              style={styles.debitorValue ||= {
                fontSize: 13,
                fontWeight: 600
              }}
              testID={`${testID}/debitor/name`}
            >
              {organization ? organization.name() : "-"}
            </Text>
          </View>
        </View>
        <View
          style={styles.footer ||= {
            alignItems: "center",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 16,
            marginTop: 10
          }}
          testID={`${testID}/footer`}
        >
          <Text
            style={styles[`date-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 12,
              fontWeight: 600
            }}
            testID={`${testID}/date`}
          >
            {formatDate(invoice.date())}
          </Text>
          <Text
            style={styles.amount ||= {
              fontSize: 14,
              fontWeight: 700,
              marginLeft: "auto"
            }}
            testID={`${testID}/amount`}
          >
            {formatMoneyFromCents(invoice.amountCents())}{invoice.amountCurrency() ? ` ${invoice.amountCurrency()}` : ""}
          </Text>
        </View>
      </Card>
    )
  }

  /**
   * Delegates delete button presses to the parent screen.
   * @returns {unknown} Delete handler result.
   * @throws {Error} When no delete handler was provided.
   */
  onDeleteButtonPress = () => {
    const {invoice, onDeletePress} = this.p
    if (!onDeletePress) throw new Error("InvoiceCard delete handler missing")
    return onDeletePress(invoice)
  }
}

/** @type {NamedExoticComponent<InvoiceCardProps>} */
const InvoiceCardComponent = memo(shapeComponent(InvoiceCard))

export default InvoiceCardComponent
